// P:\0PJ\synforge\frontend\styles\personalDisplay.ts v2 2026.07.27 23:20 - moved from localStorage to server (workspace_settings.display) so settings follow the account across devices
"use client"

import api from "@/lib/api"

export interface PersonalDisplaySettings {
  fontSize:    number
  fontFamily:  string
  meetingSize: "small" | "default" | "large"
  theme:       "dark" | "light"
  radius:      number
  customFonts: string[]
  // 자동 로그아웃 — 분 단위, 0이면 사용 안 함(비활성). 마지막 활동(마우스/키보드/
  //   터치) 이후 이 시간이 지나면 자동 로그아웃한다(2026.09.12 확정).
  autoLogoutMinutes: number
}

export const DEFAULT_DISPLAY_SETTINGS: PersonalDisplaySettings = {
  fontSize:    13,
  fontFamily:  "system",
  meetingSize: "default",
  theme:       "dark",
  radius:      12,
  customFonts: [],
  autoLogoutMinutes: 0,
}

export const BUILTIN_FONTS = [
  { id: "system",  label: "System UI",    css: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { id: "sans",    label: "Inter / Sans", css: "'Inter', 'Helvetica Neue', Arial, sans-serif"              },
  { id: "mono",    label: "Monospace",    css: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace"   },
  { id: "serif",   label: "Serif",        css: "'Georgia', 'Times New Roman', serif"                        },
  { id: "rounded", label: "Rounded",      css: "'Nunito', 'Varela Round', sans-serif"                       },
  { id: "manrope", label: "Manrope",      css: "var(--font-manrope), sans-serif"                            },
]

// 서버(계정)에서 불러온다 — 기기가 바뀌어도, 사용자가 100명이어도 계정마다 서버 1행에 저장된다.
//   비로그인 상태(401)면 기본값으로 조용히 폴백한다(TokenLoader 는 로그인 전에도 돈다).
export async function loadPersonalDisplaySettings(): Promise<PersonalDisplaySettings> {
  try {
    const res = await api.get("/workspace/settings/")
    const saved = res.data?.display
    return saved ? { ...DEFAULT_DISPLAY_SETTINGS, ...saved } : DEFAULT_DISPLAY_SETTINGS
  } catch {
    return DEFAULT_DISPLAY_SETTINGS
  }
}

// 저장 — 슬라이더를 드래그하면 onChange 가 촘촘히 여러 번 불릴 수 있어 400ms 디바운스한다.
//   화면 적용(applyPersonalDisplaySettings)은 디바운스 없이 즉시 하고, 서버 저장만 늦춘다.
let _saveTimer: ReturnType<typeof setTimeout> | null = null
export function savePersonalDisplaySettings(s: PersonalDisplaySettings) {
  if (_saveTimer) clearTimeout(_saveTimer)
  _saveTimer = setTimeout(() => {
    api.patch("/workspace/settings/", { display: s }).catch(() => {
      // 저장 실패해도 화면 적용 자체는 이미 됐으므로 조용히 무시(다음 변경 때 다시 시도됨).
    })
  }, 400)
}

export function applyPersonalDisplaySettings(s: PersonalDisplaySettings) {
  const root = document.documentElement

  // 폰트 크기 변수(다른 곳에서 참조할 수 있으므로 유지)
  root.style.setProperty("--app-font-size", `${s.fontSize}px`)

  // 폰트 패밀리
  const builtin = BUILTIN_FONTS.find(f => f.id === s.fontFamily)
  if (builtin) {
    root.style.setProperty("--app-font-family", builtin.css)
  } else if (s.customFonts.includes(s.fontFamily)) {
    root.style.setProperty("--app-font-family", `'${s.fontFamily}', sans-serif`)
  }

  // 미팅 텍스트 크기
  const base        = s.fontSize
  const meetingSize = s.meetingSize === "small" ? base - 2 : s.meetingSize === "large" ? base + 2 : base
  root.style.setProperty("--meeting-font-size", `${Math.max(meetingSize, 8)}px`)

  // 라운드
  root.style.setProperty("--app-radius", `${s.radius}px`)

  // 테마 적용
  root.setAttribute("data-theme", s.theme)

  // ── !important 오버라이드 주입 ──
  // 어드민 DesignToken(styles/tokens.ts 의 TokenLoader)이 body/p/span/.text-xs 등에
  //   font-size 를 !important 로 이미 걸어둔다. 인라인 스타일은 !important 를 못 이기므로
  //   여기서도 똑같은 방식(<style> 태그 + !important)으로 걸어야 실제로 반영된다.
  //   head 맨 끝에 매번 재삽입 - tokens.ts 스타일 태그보다 뒤에 와야
  //   (같은 !important 끼리는 나중 것이 이긴다) 개인 설정이 이긴다.
  const styleId = "synforge-personal-override"
  let styleEl = document.getElementById(styleId) as HTMLStyleElement
  if (!styleEl) {
    styleEl = document.createElement("style")
    styleEl.id = styleId
  }
  document.head.appendChild(styleEl)

  const rc = s.radius
  const rb = Math.max(Math.round(s.radius / 2), 2)

  styleEl.textContent = `
    body, p, span, div, button, input, textarea, select, a {
      font-size: ${s.fontSize}px !important;
      font-family: var(--app-font-family) !important;
    }
    .text-xs   { font-size: ${Math.max(s.fontSize - 2, 8)}px !important; }
    .text-sm   { font-size: ${Math.max(s.fontSize - 1, 9)}px !important; }
    .text-base { font-size: ${s.fontSize}px !important; }
    .text-lg   { font-size: ${s.fontSize + 2}px !important; }
    .text-xl   { font-size: ${s.fontSize + 4}px !important; }
    .text-2xl  { font-size: ${s.fontSize + 8}px !important; }
    .text-3xl  { font-size: ${s.fontSize + 12}px !important; }

    .rounded      { border-radius: ${Math.max(rb - 4, 2)}px !important; }
    .rounded-md   { border-radius: ${Math.max(rb - 2, 2)}px !important; }
    .rounded-lg   { border-radius: ${rb}px !important; }
    .rounded-xl   { border-radius: ${Math.round(rc * 0.75)}px !important; }
    .rounded-2xl  { border-radius: ${rc}px !important; }
    .rounded-3xl  { border-radius: ${rc + 4}px !important; }
    .rounded-full { border-radius: 9999px !important; }

    .meeting-preview-text { font-size: ${Math.max(meetingSize, 8)}px !important; }
  `
}